import React from 'react';
import { AffiliationFilterForm } from './AffiliatedFilter';
import dotProp from 'dot-prop-immutable';
import axiosInstance from 'utils/axiosUtil';
import { CsvUtil, DataUtil, SpecialtyUtil, CsvExportUtil } from 'doctivity-shared/utils';
import { AffiliatedIcon } from 'components';

/**
 * Format a cell value
 * @name DataMapper
 * @function
 * @param {unknown} value
 * @param {unknown} row
 * @param {string} columnName
 * @param {ColumnMapping} mapping
 */

/**
 * @typedef {Object} ColumnMapping
 * @property {string} displayName - The user-facing name (if different from the key for this entry)
 * @property {string?} helpText - Extra help text (often includes column names that will be in the download)
 * @property {string?} downloadName - The CSV column name (if different than displayName)
 * @property {string?} type - Data type (string, number, boolean)
 * @property {boolean?} displayable - Whether to include the column in the user facing table (default: true)
 * @property {boolean?} downloadable - Whether to include this column in the download (default: true)
 * @property {DataMapper?} format - Optional method to produce the output value for this column
 */
/**
 * @typedef {Function} QueryStoreApplier
 * @param {Object} store - The current redux store
 * @param {Object} query - The query to be sent to the server
 * @returns {Object} The modified query
 */
/**
 * @typedef {Object} DataDownload
 * @property {string} displayName - The user facing name of the data
 * @property {string} downloadName - The name of the download
 *   This can use the following substitutions: {date}: dd-MM-yyyy will be inserted
 * @property {string} endpoint - The endpoint which the initialQuery is valid against
 * @property {Array} initialQuery - The initial query structure
 * @property {Object?} filterForm - An optional extra form for specific filtering
 * @property {Object.<string, ColumnMapping>} columnMapping - The mapping from query column names to user-facing columns names
 * @property {QueryStoreApplier?} applyStore - An optional method which can extract values from the current store and apply them to the query, executed before the query is sent to the server
 **/

/** @type {DataDownload[]} */
const availableDownloads = [
  {
    displayName: 'Provider Mailing Addresses',
    helpText:
      'Download a .csv of Provider Mailing Addresses. Fields: NPI, First Name, Last Name, Credential, Location Name, Specialty, Address 1, Address 2, City, State, Zip, Email',
    endpoint: 'Providers',
    downloadName: 'doctivity-providers-address_{date}.csv',
    filterForm: AffiliationFilterForm,
    applyStore: (store, query) => {
      const {
        app: { selectedClient },
      } = store;
      const newQuery = DataUtil.deepCopy(query);
      newQuery.include.push({
        association: 'related_clients',
        through: {
          as: 'options',
          attributes: ['is_affiliated', 'rvu_goal', 'email'],
        },
        where: {
          id: selectedClient,
        },
        required: false,
      });
      return newQuery;
    },
    initialQuery: {
      where: {},
      include: [
        {
          association: 'clients',
          attributes: ['id', 'name'],
        },
        {
          association: 'taxonomy',
        },
        {
          association: 'location',
        },
      ],
      order: [['id', 'ASC']],
    },
    columnMapping: {
      clients: {
        displayName: '',
        downloadName: 'Affiliated',
        sortable: false,
        filter: false,
        style: { width: 50 },
        format: (v, _row, _key, mapping) => {
          if (Array.isArray(v) && v.length > 0) {
            return typeof mapping === 'object' &&
              mapping.downloadName !== undefined ? (
              'affiliated'
            ) : (
              <AffiliatedIcon />
            );
          }
        },
      },
      npi: {
        displayName: 'NPI',
        displayable: false,
      },
      last_name: {
        displayName: 'Last Name',
      },
      first_name: {
        displayName: 'First Name',
      },
     'taxonomy.classification': {
        displayName: 'Specialty',
        downloadable: false,
        format: (_, row) => row.taxonomy ? SpecialtyUtil.getNameFromTaxonomy(row.taxonomy) : '',
        filterFields: ['primary_taxonomy'],
        specialtySearch: true,
      },      
      'taxonomy.classification2': {
        displayName: 'Specialty',
        displayable: false,
        downloadable: true,
        format: (_, row) => row.taxonomy ? SpecialtyUtil.getNameFromTaxonomy(row.taxonomy) : '',
      },
      'taxonomy.classification3': {
        displayName: 'Code',
        displayable: false,
        downloadable: true,
        format: (_, row) => row.taxonomy?.code || '',
      },
      credential: {
        displayName: 'Credential',
        displayable: false,
      },
      'location.name': {
        displayName: 'Location Name',
        displayable: false,
      },
      'location.address1': {
        displayName: 'Address',
        downloadName: 'Address 1',
      },
      'location.address2': {
        displayName: 'Address 2',
        displayable: false,
      },
      'location.city': {
        displayName: 'City',
      },
      'location.state': {
        displayName: 'State',
        format: (v) => v?.toUpperCase(),
        style: { width: 50 },
      },
      'location.postal_code': {
        displayName: 'Zip',
        format: (v) => `000000${String(v)}`.slice(-5),
        style: { width: 80 },
      },
      'related_clients': {
        displayable: false,
        displayName: 'Email',
        format: (v, row) => {
          if (Array.isArray(v) && v.length > 0) {
            return v[0].options?.email ?? row.email;
          }
          return row.email;
        }
      }
    },
  },
];

/**
 * Returns the DynamicTable columns for the given download type
 * @param {string} index - The index of the entry in availableDownloads to extract column definitions from
 */
function getColumns(idx) {
  const entry = availableDownloads[idx];
  return Object.entries(entry.columnMapping)
    .map(([name, e]) => {
      if (e.displayable === false) return undefined;

      return {
        key: name,
        label: e.displayName ?? name,
        sortable: e.sortable,
        filter: e.filter,
        format: e.format,
        style: e.style,
        filterFields: e.filterFields,
        specialtySearch: e.specialtySearch,
        customFilterLabel: e.cystomFilterLabel,
        customFilterText: e.customFilterText
      };
    })
    .filter((v) => v !== undefined);
}

/**
 * @param {DataDownload} downloadInfo
 * @param {Object} query
 * @param {Object} store
 */
/**
 * @param {DataDownload} downloadInfo
 * @param {Object} query
 * @param {Object} store
 */
async function getCSV(downloadInfo, query, store, options = {}) {
  let dataQuery = DataUtil.deepCopy(query);
  // Row count / start row come from the CSV export dialog. Clamp to the per-export cap
  // (CsvExportUtil, keyed by endpoint) — 5000 was overrunning the ~6 MB response wall in
  // prod. The backend has no cap on this JSON fetch, so the clamp here is the guard.
  const offset = parseInt(options.offset, 10);
  dataQuery.offset = Number.isFinite(offset) && offset > 0 ? offset : 0;
  dataQuery.limit = CsvExportUtil.clampLimit(options.limit, downloadInfo.endpoint);
  if (downloadInfo.applyStore) {
    dataQuery = downloadInfo.applyStore(store, dataQuery);
  }
  
  try {
    const results = await axiosInstance.get(`/${downloadInfo.endpoint}`, {
      params: {
        opts: dataQuery,
      },
      timeout: 60000, // 60 second timeout (increase from default)
    });
    
    if (!results || !Array.isArray(results.data?.rows)) {
      throw new Error('Unable to retrieve data for download. Please try again.');
    }
    
    return convertToCSV(downloadInfo, results.data.rows);
  } catch (error) {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      throw new Error('Download request timed out. Please try with fewer filters or contact support.');
    }
    throw error;
  }
}

/**
 * @param {DataDownload} downloadInfo
 * @param {Array} data
 */
function convertToCSV(downloadInfo, rows) {
  const columnMap = Object.entries(downloadInfo.columnMapping)
    .filter(
      ([, mapping]) =>
        mapping.downloadable || mapping.downloadable === undefined,
    )
    .reduce((acc, [key, mapping]) => {
      acc[key] = mapping;
      return acc;
    }, {});
  if (!rows || !Array.isArray(rows)) {
    throw new Error('Unable to use data for download. Please try again.');
  }
  const csvData = [];
  const csvIndexMap = {};
  rows.forEach((row, idx) => {
    if (idx === 0) {
      const header = [];
      Object.keys(columnMap).forEach((key) => {
        csvIndexMap[key] = columnMap[key];
        header.push(columnMap[key].downloadName ?? columnMap[key].displayName);
      });
      csvData.push(header);
    }
    const formattedRow = [];
    Object.entries(csvIndexMap).forEach(([key, mapping]) => {
      const value = dotProp.get(row, key);
      let formatted = mapping.format
        ? mapping.format(value, row, key, mapping)
        : value;
      if (typeof formatted === 'string') {
        formatted = `"${formatted.replace('"', '"')}"`;
      }
      formattedRow.push(formatted);
    });
    csvData.push(formattedRow);
  });
  const csv = CsvUtil.matrixToString(csvData);
  return csv;
}

export { getColumns, availableDownloads, getCSV, convertToCSV };